import { useEffect } from 'react'
import cx from 'classnames'
import {
  SanityAccordionsBlock,
  SanityBlocks,
  SanityBlockType,
  SanityBlogPostCardBlock,
  SanityContentCarouselBlock,
  SanityFreeformBlock,
  SanityNewsletterBlock,
  SanityVideoBlock,
  SanityCalendlyWidgetBlock,
  SanityDemoFormBlock,
  SanityIframeBlock,
  SanityHtmlBlock,
  SanityMediaTextBlock,
} from '@data/sanity/queries/types/blocks'
import Accordions from '@blocks/accordions'
import BlogPostCard from '@blocks/blog/blog-post-card'
import CalendlyWidget from '@blocks/calendly-widget'
import DemoForm from '@blocks/demo-form'
import Freeform from '@blocks/freeform'
import MediaText from '@blocks/media-text'
import Newsletter from '@blocks/newsletter'
import VideoModule from './video'
import Carousel from '@components/carousel'

interface GridBlockProps {
  block: SanityBlocks
}

const GridBlock = ({ block }: GridBlockProps) => {
  useEffect(() => {
    // A quick fix for executing scripts from embedded HTML
    if (block._type === SanityBlockType.HTML) {
      const { code } = block as SanityHtmlBlock

      if (code) {
        const tempDiv = document.createElement('div')
        tempDiv.innerHTML = code
        const scripts = tempDiv.querySelectorAll('script')
        scripts.forEach((script) => {
          const newScript = document.createElement('script')
          newScript.textContent = script.textContent
          document.body.appendChild(newScript)
        })
      }
    }
  }, [block])

  switch (block._type as SanityBlockType) {
    case SanityBlockType.FREEFORM: {
      const freeformBlock = block as SanityFreeformBlock

      return (
        <Freeform
          content={freeformBlock.content}
          textAlign={freeformBlock.textAlign}
          maxWidth={freeformBlock.maxWidth}
        />
      )
    }

    case SanityBlockType.ACCORDIONS: {
      const accordionsBlock = block as SanityAccordionsBlock

      return <Accordions items={accordionsBlock.items} />
    }

    case SanityBlockType.VIDEO: {
      const videoBlock = block as SanityVideoBlock

      return (
        <VideoModule
          type={videoBlock.type}
          muxVideo={videoBlock.muxVideo}
          settings={videoBlock.settings}
          aspectRatio={videoBlock.aspectRatio}
          borderRadius={videoBlock.borderRadius}
          thumbnailImage={videoBlock.thumbnailImage}
        />
      )
    }

    case SanityBlockType.NEWSLETTER: {
      const newsletterBlock = block as SanityNewsletterBlock

      return (
        <Newsletter
          id={newsletterBlock._key}
          title={newsletterBlock.title}
          description={newsletterBlock.description}
          variant={newsletterBlock.variant}
          service={newsletterBlock.service}
          hubSpotFormId={newsletterBlock.hubSpotFormId}
          klaviyoListID={newsletterBlock.klaviyoListID}
          submit={newsletterBlock.submit}
          successMsg={newsletterBlock.successMsg}
          errorMsg={newsletterBlock.errorMsg}
          terms={newsletterBlock.terms}
        />
      )
    }

    case SanityBlockType.BLOG_POST_CARD: {
      const blogPostCardBlock = block as SanityBlogPostCardBlock

      const { post, options } = blogPostCardBlock

      return <BlogPostCard post={post} options={options} />
    }

    case SanityBlockType.CONTENT_CAROUSEL: {
      const contentCarouselBlock = block as SanityContentCarouselBlock
      const {
        items,
        drag,
        loop,
        showArrows,
        autoSwitch,
        autoSwitchTimeout,
        previewNextSlide,
        showOverflow,
      } = contentCarouselBlock

      return (
        <Carousel
          items={items}
          options={{
            selector: '.content-slide',
            drag,
            loop,
          }}
          showArrows={showArrows}
          autoSwitch={autoSwitch}
          autoSwitchTimeout={autoSwitchTimeout}
          previewNextSlide={previewNextSlide}
          showOverflow={showOverflow}
        />
      )
    }

    case SanityBlockType.CALENDLY_WIDGET: {
      const calendlyWidgetBlock = block as SanityCalendlyWidgetBlock

      const { url, hideEventDetails, hideCookieSettings } = calendlyWidgetBlock

      return (
        <CalendlyWidget
          url={url}
          hideEventDetails={hideEventDetails}
          hideCookieSettings={hideCookieSettings}
        />
      )
    }

    case SanityBlockType.DEMO_FORM: {
      const {
        _key,
        service,
        hubSpotFormId,
        hubSpotNewsletterFormId,
        klaviyoListID,
        klaviyoNewsletterListID,
        hidePhoneInput,
        submit,
        successMsg,
        errorMsg,
        terms,
        newsletterStatement,
        analytics,
        buttonStyle,
        strings,
      } = block as SanityDemoFormBlock

      return (
        <DemoForm
          id={_key}
          service={service}
          hubSpotFormId={hubSpotFormId}
          hubSpotNewsletterFormId={hubSpotNewsletterFormId}
          klaviyoListID={klaviyoListID}
          klaviyoNewsletterListID={klaviyoNewsletterListID}
          hidePhoneInput={hidePhoneInput}
          submit={submit}
          successMsg={successMsg}
          errorMsg={errorMsg}
          terms={terms}
          newsletterStatement={newsletterStatement}
          analytics={analytics}
          buttonStyle={buttonStyle}
          strings={strings}
        />
      )
    }

    case SanityBlockType.IFRAME: {
      const iframeBlock = block as SanityIframeBlock

      const { title, url, width, height } = iframeBlock

      return (
        <iframe
          title={title}
          src={url}
          className={cx({ 'w-full': !width })}
          width={width}
          height={height}
        />
      )
    }

    case SanityBlockType.HTML: {
      const htmlBlock = block as SanityHtmlBlock

      const { code } = htmlBlock

      return code ? <div dangerouslySetInnerHTML={{ __html: code }} /> : null
    }

    case SanityBlockType.MEDIA_TEXT: {
      const mediaTextBlock = block as SanityMediaTextBlock

      return (
        <MediaText
          mediaType={mediaTextBlock.mediaType}
          image={mediaTextBlock.image}
          video={mediaTextBlock.video}
          content={mediaTextBlock.content}
        />
      )
    }
  }
}

export default GridBlock
